import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { fetchUpcomingEvents } from "@/lib/api";
import AddToCalendarButton from "./AddToCalendarButton";

const ECLIPSE_CONFIG = {
  solar_eclipse: {
    gradient: "linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(234, 88, 12, 0.08) 50%, rgba(220, 38, 38, 0.12) 100%)",
    borderColor: "rgba(251, 146, 60, 0.25)",
    iconBg: "rgba(251, 146, 60, 0.15)",
    accentColor: "#FB923C",
    glowColor: "rgba(251, 146, 60, 0.3)",
    icon: "🌘",
    label: "Solar Eclipse",
    description: "The Sun's light will be temporarily obscured as the Moon passes between Earth and Sun.",
  },
  lunar_eclipse: {
    gradient: "linear-gradient(135deg, rgba(244, 63, 94, 0.15) 0%, rgba(190, 18, 60, 0.08) 50%, rgba(124, 58, 237, 0.12) 100%)",
    borderColor: "rgba(244, 63, 94, 0.25)",
    iconBg: "rgba(244, 63, 94, 0.15)",
    accentColor: "#F43F5E",
    glowColor: "rgba(244, 63, 94, 0.3)",
    icon: "🌒",
    label: "Lunar Eclipse",
    description: "Earth's shadow will fall upon the Moon, creating a dramatic celestial display.",
  },
};

function formatCountdown(targetDate) {
  const now = new Date();
  const target = new Date(targetDate);
  const diff = target - now;

  if (diff <= 0) return { display: "Now", days: 0, hours: 0, minutes: 0, seconds: 0, urgent: true };

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return { days, hours, minutes, seconds, urgent: days < 7 };
}

function CountdownUnit({ value, label, accentColor, size = "normal" }) {
  const textSize = size === "small" ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl md:text-4xl";
  const labelSize = size === "small" ? "text-[9px]" : "text-[10px] sm:text-xs";
  
  return (
    <div className="flex flex-col items-center">
      <div className={`${textSize} font-light tabular-nums`} style={{ color: accentColor }}>
        {String(value).padStart(2, "0")}
      </div>
      <div className={`${labelSize} uppercase tracking-wider text-white/40 mt-0.5`}>
        {label}
      </div>
    </div>
  );
}

function EclipseCard({ eclipse, countdown, isExpanded, onToggle, size = "normal" }) {
  const config = ECLIPSE_CONFIG[eclipse.type] || ECLIPSE_CONFIG.solar_eclipse;
  
  const localDate = new Date(eclipse.timestamp_utc).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const localTime = new Date(eclipse.timestamp_utc).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

  const iconSize = size === "small" ? "w-10 h-10 text-lg" : "w-12 h-12 sm:w-14 sm:h-14 text-xl sm:text-2xl";
  const titleSize = size === "small" ? "text-base sm:text-lg" : "text-lg sm:text-xl";
  const padding = size === "small" ? "p-4" : "p-5 sm:p-6";

  return (
    <div
      className={`rounded-xl overflow-hidden ${padding}`}
      style={{
        background: config.gradient,
        border: `1px solid ${config.borderColor}`,
        boxShadow: `0 0 30px ${config.glowColor}`,
      }}
      data-testid={`eclipse-card-${eclipse.type}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`${iconSize} rounded-full flex items-center justify-center flex-shrink-0`}
          style={{ background: config.iconBg }}
        >
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[9px] sm:text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ background: config.iconBg, color: config.accentColor }}
            >
              {config.label}
            </span>
            {countdown.urgent && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                Soon
              </span>
            )}
          </div>
          <h3 className={`${titleSize} font-light text-white/95 leading-tight`}>
            {eclipse.title}
          </h3>
          <p className="text-white/40 text-xs sm:text-sm mt-0.5">
            {eclipse.subtitle}
          </p>
        </div>
      </div>

      {/* Countdown */}
      <div className="flex justify-center gap-2 sm:gap-4 mb-4">
        <CountdownUnit value={countdown.days} label="Days" accentColor={config.accentColor} size={size} />
        <div className="text-lg sm:text-xl text-white/20 self-start mt-1">:</div>
        <CountdownUnit value={countdown.hours} label="Hrs" accentColor={config.accentColor} size={size} />
        <div className="text-lg sm:text-xl text-white/20 self-start mt-1">:</div>
        <CountdownUnit value={countdown.minutes} label="Min" accentColor={config.accentColor} size={size} />
        <div className="text-lg sm:text-xl text-white/20 self-start mt-1 hidden sm:block">:</div>
        <div className="hidden sm:block">
          <CountdownUnit value={countdown.seconds} label="Sec" accentColor={config.accentColor} size={size} />
        </div>
      </div>

      {/* Date/Time */}
      <div className="text-center mb-3">
        <p className="text-white/60 text-xs sm:text-sm">{localDate}</p>
        <p className="text-white/30 text-[10px] sm:text-xs">{localTime}</p>
      </div>

      {/* Expandable */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-center gap-1 text-white/40 hover:text-white/60 transition-colors text-xs pt-2 border-t border-white/5"
        data-testid={`eclipse-expand-${eclipse.type}`}
      >
        <span>{isExpanded ? "Less" : "What to expect"}</span>
        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 text-white/50 text-xs sm:text-sm leading-relaxed">
              <p>{config.description}</p>
              <p className="mt-2">{eclipse.interpretation}</p>
              <p className="text-white/25 text-[10px] mt-2 italic">
                General astrological themes — not personalised predictions.
              </p>
              
              {/* Add to Calendar */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <AddToCalendarButton event={eclipse} variant="compact" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function EclipseCountdownWidget({ maxDays = 30 }) {
  const [eclipses, setEclipses] = useState([]);
  const [countdowns, setCountdowns] = useState({});
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [expandedType, setExpandedType] = useState(null);

  // Load upcoming eclipses
  useEffect(() => {
    async function loadEclipses() {
      try {
        const response = await fetchUpcomingEvents(50, maxDays, null);
        const allEclipses = response.events.filter(
          (e) => e.type === "solar_eclipse" || e.type === "lunar_eclipse"
        );
        
        // Get one of each type (next solar and next lunar)
        const nextSolar = allEclipses.find(e => e.type === "solar_eclipse");
        const nextLunar = allEclipses.find(e => e.type === "lunar_eclipse");
        
        const result = [];
        if (nextSolar) result.push(nextSolar);
        if (nextLunar) result.push(nextLunar);
        
        // Sort by date
        result.sort((a, b) => new Date(a.timestamp_utc) - new Date(b.timestamp_utc));
        
        setEclipses(result);
      } catch (err) {
        console.error("Failed to load eclipse data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadEclipses();
  }, [maxDays]);

  // Update countdowns every second
  useEffect(() => {
    if (eclipses.length === 0) return;

    const updateCountdowns = () => {
      const newCountdowns = {};
      eclipses.forEach(eclipse => {
        newCountdowns[eclipse.type] = formatCountdown(eclipse.timestamp_utc);
      });
      setCountdowns(newCountdowns);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [eclipses]);

  // Check sessionStorage for dismissal
  useEffect(() => {
    if (eclipses.length > 0) {
      const dismissKey = `eclipses_dismissed_${eclipses.map(e => e.id).join("_")}`;
      const wasDismissed = sessionStorage.getItem(dismissKey);
      if (wasDismissed) {
        setDismissed(true);
      }
    }
  }, [eclipses]);

  const handleDismiss = () => {
    if (eclipses.length > 0) {
      const dismissKey = `eclipses_dismissed_${eclipses.map(e => e.id).join("_")}`;
      sessionStorage.setItem(dismissKey, "true");
    }
    setDismissed(true);
  };

  const handleToggle = (type) => {
    setExpandedType(prev => prev === type ? null : type);
  };

  if (loading || eclipses.length === 0 || dismissed) {
    return null;
  }

  const isSingle = eclipses.length === 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="relative"
        data-testid="eclipse-countdown-widget"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl sm:text-3xl font-light text-white/90">
              {isSingle ? "Upcoming Eclipse" : "Upcoming Eclipses"}
            </h2>
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-purple-500/15 text-purple-400 border border-purple-500/20">
              Celestial Event{!isSingle && "s"}
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-2 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
            data-testid="eclipse-dismiss-btn"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Eclipse Cards */}
        <div className={`grid gap-4 ${isSingle ? "grid-cols-1 max-w-xl" : "grid-cols-1 md:grid-cols-2"}`}>
          {eclipses.map((eclipse) => (
            <EclipseCard
              key={eclipse.id}
              eclipse={eclipse}
              countdown={countdowns[eclipse.type] || { days: 0, hours: 0, minutes: 0, seconds: 0 }}
              isExpanded={expandedType === eclipse.type}
              onToggle={() => handleToggle(eclipse.type)}
              size={isSingle ? "normal" : "small"}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
